import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { DashboardContent } from '@/layouts/dashboard';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { liveChallengesApi, liveChallengeActivationsApi } from '@/services/liveChallenges';

const normalizeJsonApi = items =>
  items.map(item => ({
    id: item.id,
    ...Object.fromEntries(Object.entries(item.attributes).map(([k, v]) => [k.replace(/-/g, '_'), v]))
  }));

// Detalhe expandido de uma participação: ranking congelado da ativação + meu resumo.
function ParticipationDetail({ activationId }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    liveChallengeActivationsApi
      .myResult(activationId)
      .then(response => {
        if (!active) return;
        // RECONCILIAÇÃO backend: my_result é manual snake_case, sem wrapper attributes.
        const attrs = response?.data ?? {};
        setDetail(Object.fromEntries(Object.entries(attrs).map(([k, v]) => [k.replace(/-/g, '_'), v])));
      })
      .catch(() => {
        if (active) toast.error('Erro ao carregar resultado da ativação.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [activationId]);

  if (loading) return <Loader />;
  if (!detail) return null;

  return (
    <div className="bg-neutral-lightest/40 mt-3 rounded-xl p-4">
      <p className="m-0 text-sm font-semibold text-neutral-dark">
        Você acertou {detail.correct_count ?? 0} de {detail.total_questions ?? 0} questões
      </p>
      {Array.isArray(detail.leaderboard) && detail.leaderboard.length > 0 && (
        <ol className="mt-2 list-none space-y-1 p-0">
          {detail.leaderboard.map(entry => (
            <li
              key={`${entry.rank}-${entry.name}`}
              className="flex items-center justify-between text-xs text-neutral"
            >
              <span>
                {entry.rank}º {entry.name}
              </span>
              <span className="font-semibold">{entry.total_score} pts</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default function MyChallengesPage() {
  const [participations, setParticipations] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const fetchPage = useCallback(async page => {
    setLoading(true);
    try {
      const response = await liveChallengesApi.participations(page);
      setParticipations(normalizeJsonApi(response?.data ?? []));
      const meta = response?.meta ?? {};
      // RECONCILIAÇÃO backend: meta vem em snake_case (FastJsonapi não transforma meta).
      setPagination({ page: meta.current_page ?? page, totalPages: meta.total_pages ?? 1 });
    } catch {
      toast.error('Erro ao carregar seu histórico de desafios.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  const breadcrumbs = {
    title: 'Aprendizado Dinâmico',
    nav: [{ route: '/desafio-ao-vivo/meus', name: 'Meus desafios', isActive: true }]
  };

  const formatDateTime = value => (value ? new Date(value).toLocaleString('pt-BR') : '—');

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      {loading ? (
        <Loader />
      ) : participations.length === 0 ? (
        <EmptyState
          type="data"
          text="Você ainda não participou de nenhum desafio ao vivo."
        />
      ) : (
        <>
          <ul className="overflow-hidden rounded-xl bg-white shadow-dropshadow-sm [&>li:last-child]:border-b-0">
            {participations.map(participation => {
              const expanded = expandedId === participation.id;
              return (
                <li
                  key={participation.id}
                  className="border-b border-neutral-lightest px-5 py-3"
                >
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-6">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="m-0 truncate text-sm font-semibold text-neutral-dark">{participation.challenge_title}</h3>
                        {participation.rank != null && (
                          <Badge
                            variant={participation.rank <= 3 ? 'success-light' : 'neutral-lightest'}
                            size="xs"
                          >
                            {participation.rank}º lugar
                          </Badge>
                        )}
                      </div>
                      <p className="mt-0.5 text-xs text-neutral">
                        {formatDateTime(participation.activation_started_at)} &middot; Host: {participation.host_name || '—'} &middot; {participation.total_score ?? 0} pts
                      </p>
                    </div>
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        size="xs"
                        onClick={() => setExpandedId(expanded ? null : participation.id)}
                      >
                        {expanded ? <FiChevronUp /> : <FiChevronDown />} {expanded ? 'Fechar' : 'Ver resultado'}
                      </Button>
                    </div>
                  </div>
                  {expanded && <ParticipationDetail activationId={participation.activation_id} />}
                </li>
              );
            })}
          </ul>
          {pagination.totalPages > 1 && (
            <div className="mt-4 flex justify-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="xs"
                disabled={pagination.page <= 1}
                onClick={() => fetchPage(pagination.page - 1)}
              >
                Anterior
              </Button>
              <span className="self-center text-xs text-neutral">
                {pagination.page} / {pagination.totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="xs"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => fetchPage(pagination.page + 1)}
              >
                Próxima
              </Button>
            </div>
          )}
        </>
      )}
    </DashboardContent>
  );
}
